/**
 * @website-builder/blob-store — one content-addressed object store, three homes.
 *
 * A `BlobStore` is a minimal S3-shaped key→bytes store. The same interface is
 * implemented over:
 *   - OPFS in the browser (local-first)  — ./opfs  (OpfsBlobStore)
 *   - real S3 on the server              — ./s3    (S3BlobStore)
 *   - memory (tests / the in-browser facade default) — MemBlobStore (here)
 *
 * Keys use the served object convention so local ↔ cloud ↔ export all map 1:1:
 * images live at `_assets/<sha256>` (no extension — the sha IS the identity).
 * Because keys are content addresses, `syncBlobStores` is a pure
 * copy-what's-missing (the `aws s3 sync` of immutable blobs).
 */

export const ASSET_PREFIX = "_assets/";

/** Served/object key for a content-addressed blob (no extension). */
export function assetKey(sha256: string): string {
  return `${ASSET_PREFIX}${sha256}`;
}

/** MIME → file extension, shared by every asset store (web OPFS, native,
 *  builder-mcp) so they agree on a stored asset's `ext`. */
const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/ogg": "ogv",
  "video/quicktime": "mov",
  "audio/mpeg": "mp3",
  "audio/ogg": "oga",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/webm": "weba",
  "audio/mp4": "m4a",
  "audio/aac": "aac",
  "audio/flac": "flac",
};

export function extForMime(mime: string): string {
  return MIME_TO_EXT[mime] ?? "bin";
}

/** hex SHA-256 of some bytes — the content address. */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export interface BlobStore {
  has(key: string): Promise<boolean>;
  /** Bytes, or null if absent. */
  get(key: string): Promise<Uint8Array | null>;
  /** A Blob (disk-backed/lazy where possible), or null if absent. */
  getBlob(key: string): Promise<Blob | null>;
  put(key: string, data: Uint8Array | Blob): Promise<void>;
  /** Keys, optionally restricted to those starting with `prefix`. */
  list(prefix?: string): Promise<string[]>;
  delete(key: string): Promise<void>;
}

export interface SyncResult {
  readonly copied: string[];
  readonly skipped: string[];
  readonly failed: ReadonlyArray<{ key: string; error: string }>;
}

/**
 * Copy every key in `src` that's missing from `dst` (the `aws s3 sync` of a
 * content-addressed bucket — blobs are immutable by hash, so a present key is
 * already correct and is skipped). Returns what was copied/skipped/failed.
 *
 * `opts.keys` syncs exactly those keys instead of listing the source (use when
 * only a subset is relevant — e.g. the images a site references, not every
 * blob in the bucket). `opts.concurrency` bounds in-flight copies (default 1).
 */
export async function syncBlobStores(
  src: BlobStore,
  dst: BlobStore,
  opts?: { prefix?: string; keys?: string[]; concurrency?: number },
): Promise<SyncResult> {
  const copied: string[] = [];
  const skipped: string[] = [];
  const failed: { key: string; error: string }[] = [];
  const keys = opts?.keys ?? (await src.list(opts?.prefix));

  const copyOne = async (key: string): Promise<void> => {
    try {
      if (await dst.has(key)) {
        skipped.push(key);
        return;
      }
      const blob = await src.getBlob(key);
      if (!blob) {
        failed.push({ key, error: "source vanished" });
        return;
      }
      await dst.put(key, blob);
      copied.push(key);
    } catch (e) {
      failed.push({ key, error: e instanceof Error ? e.message : String(e) });
    }
  };

  const limit = Math.max(1, opts?.concurrency ?? 1);
  if (limit === 1) {
    for (const key of keys) await copyOne(key);
  } else {
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < keys.length) await copyOne(keys[cursor++]!);
    };
    await Promise.all(
      Array.from({ length: Math.min(limit, keys.length) }, worker),
    );
  }
  return { copied, skipped, failed };
}

async function toBytes(data: Uint8Array | Blob): Promise<Uint8Array> {
  // isView, not instanceof: bytes made in another realm (a worker, an
  // iframe, jsdom's TextEncoder) are not instances of this realm's
  // Uint8Array, and would be mistaken for a Blob.
  return ArrayBuffer.isView(data)
    ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    : new Uint8Array(await data.arrayBuffer());
}

/** In-memory BlobStore — the test double and the in-browser facade's fallback. */
export class MemBlobStore implements BlobStore {
  private readonly map = new Map<string, Uint8Array>();

  async has(key: string): Promise<boolean> {
    return this.map.has(key);
  }
  async get(key: string): Promise<Uint8Array | null> {
    return this.map.get(key) ?? null;
  }
  async getBlob(key: string): Promise<Blob | null> {
    const b = this.map.get(key);
    // ArrayBuffer (not the DOM-only BlobPart) so this source also
    // typechecks under server tsconfigs that don't include the DOM lib.
    return b ? new Blob([b as unknown as ArrayBuffer]) : null;
  }
  async put(key: string, data: Uint8Array | Blob): Promise<void> {
    this.map.set(key, await toBytes(data));
  }
  async list(prefix?: string): Promise<string[]> {
    const keys = [...this.map.keys()];
    return prefix ? keys.filter((k) => k.startsWith(prefix)) : keys;
  }
  async delete(key: string): Promise<void> {
    this.map.delete(key);
  }
}
